import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { handleFetchResponse } from '../utils/apiClient'
import { API_URL } from '../config'
import { GitHubRepository } from '../types'

interface RepositoriesContextType {
  repositories: GitHubRepository[]
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
  hasRepositories: boolean
}

const RepositoriesContext = createContext<RepositoriesContextType | undefined>(undefined)

interface RepositoriesProviderProps {
  children: ReactNode
}

export const RepositoriesProvider: React.FC<RepositoriesProviderProps> = ({ children }) => {
  const [repositories, setRepositories] = useState<GitHubRepository[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasFetched, setHasFetched] = useState(false)
  const { user } = useAuth()

  const fetchRepositories = async (force = false) => {
    if (!user || (hasFetched && !force)) return

    try {
      setIsLoading(true)
      setError(null)

      const token = localStorage.getItem('github_access_token')
      const response = await handleFetchResponse(await fetch(`${API_URL}/api/repositories/connected`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }))

      if (!response.ok) {
        throw new Error('Failed to fetch repositories')
      }

      const reposData = await response.json()
      const validRepos = Array.isArray(reposData) ? reposData : []
      
      setRepositories(validRepos)
      setHasFetched(true)
    } catch (err) {
      console.error('Failed to fetch repositories:', err)
      setError('Failed to load repositories')
      setRepositories([])
    } finally {
      setIsLoading(false)
    }
  }

  const refetch = async () => {
    await fetchRepositories(true)
  }

  useEffect(() => {
    if (user && !hasFetched) {
      fetchRepositories()
    } else if (!user) {
      // Reset state when user logs out
      setRepositories([])
      setHasFetched(false)
      setError(null)
    }
  }, [user, hasFetched])

  const hasRepositories = repositories.length > 0

  const value: RepositoriesContextType = {
    repositories,
    isLoading,
    error,
    refetch,
    hasRepositories
  }

  return (
    <RepositoriesContext.Provider value={value}>
      {children}
    </RepositoriesContext.Provider>
  )
}

export const useRepositories = (): RepositoriesContextType => {
  const context = useContext(RepositoriesContext)
  if (context === undefined) {
    throw new Error('useRepositories must be used within a RepositoriesProvider')
  }
  return context
}
